import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div style={{
        position: 'fixed', bottom: '24px', right: '24px', zIndex: 99999,
        display: 'flex', flexDirection: 'column', gap: '10px', pointerEvents: 'none',
        left: '24px', alignItems: 'flex-end', // Add left: 24px and flex-end to allow centering/proper positioning
      }}>
        <style>{`
          @media (max-width: 640px) {
            div[style*="zIndex: 99999"] {
              right: 16px !important;
              left: 16px !important;
              bottom: 16px !important;
              align-items: stretch !important;
            }
          }
        `}</style>
        {toasts.map(toast => (
          <Toast key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const STYLES = {
  success: { border: '#10b981', icon: '✓', iconColor: '#10b981' },
  error: { border: '#ef4444', icon: '✕', iconColor: '#ef4444' },
  info: { border: '#3b82f6', icon: 'ℹ', iconColor: '#60a5fa' },
};

const Toast = ({ toast, onRemove }) => {
  const s = STYLES[toast.type] || STYLES.info;
  return (
    <div style={{
      background: '#1f2937',
      border: `1px solid ${s.border}`,
      borderLeft: `4px solid ${s.border}`,
      borderRadius: '8px',
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '280px',
      maxWidth: '420px',
      width: '100%',
      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
      pointerEvents: 'all',
      animation: 'toastSlideIn 0.25s ease',
    }}>
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <span style={{ color: s.iconColor, fontSize: '16px', fontWeight: 'bold', flexShrink: 0, width: '20px', textAlign: 'center' }}>
        {s.icon}
      </span>
      <span style={{ color: '#f3f4f6', fontSize: '14px', flex: 1, lineHeight: '1.4' }}>
        {toast.message}
      </span>
      <button
        onClick={() => onRemove(toast.id)}
        style={{
          background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer',
          fontSize: '24px', lineHeight: 1, padding: '8px', flexShrink: 0,
          width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >
        ×
      </button>
    </div>
  );
};
